import { useTranslation } from 'react-i18next'
import { Loading, Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getCustomersByIndustry } from '../services'
import { CustomerPipelineFilters } from '../types'
import { Info } from 'lucide-react'
import {
    Tooltip as UITooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import {
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    ResponsiveContainer,
    Tooltip,
} from 'recharts'

interface IndustryRadarChartProps {
    filters: CustomerPipelineFilters
}

const IndustryRadarChart: React.FC<IndustryRadarChartProps> = ({ filters }) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'by-industry',
                filters.gte,
                filters.lte,
                filters.sales_manager_codes,
            ],
            queryFn: () => getCustomersByIndustry(filters),
            enabled: !!filters.gte && !!filters.lte,
        },
        {
            loadingComponent: (
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                    <div className="flex justify-center items-center h-[300px]">
                        <Loading.CircularProgress width={32} height={32} />
                    </div>
                </div>
            ),
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    // Prepare data for radar chart - limit to top 8 industries
    const industries = data?.data?.slice(0, 8) || []

    // Capitalize first letter of each word
    const capitalize = (str: string) =>
        str
            .toLowerCase()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')

    // Transform data for recharts
    const chartData = industries.map((item) => ({
        industry: capitalize(item.industry || 'Unknown'),
        count: item.count,
        fullMark: Math.max(...industries.map((i) => i.count), 1) * 1.2,
    }))

    return (
        <div className="bg-white rounded-md p-4 border border-gray-100 shadow-sm h-full flex flex-col">
            <div className="flex items-center justify-between mb-2">
                <Typogaphy className="font-semibold text-primary capitalize">
                    {t('customer_summary.customers_by_industry')}
                </Typogaphy>
                <TooltipProvider>
                    <UITooltip>
                        <TooltipTrigger asChild>
                            <button className="text-gray-400 hover:text-gray-600">
                                <Info className="w-4 h-4" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="max-w-xs text-xs">
                                {t(
                                    'customer_summary.customers_by_industry_tooltip'
                                )}
                            </p>
                        </TooltipContent>
                    </UITooltip>
                </TooltipProvider>
            </div>

            {industries.length === 0 ? (
                <div className="flex items-center justify-center flex-1 text-gray-400 text-sm">
                    {t('no_data')}
                </div>
            ) : (
                <ResponsiveContainer
                    width="100%"
                    height="100%"
                    className="flex-1"
                >
                    <RadarChart
                        cx="50%"
                        cy="50%"
                        outerRadius="70%"
                        data={chartData}
                    >
                        <PolarGrid stroke="#e5e7eb" />
                        <PolarAngleAxis
                            dataKey="industry"
                            tick={{ fontSize: 11 }}
                        />
                        <PolarRadiusAxis
                            angle={90}
                            domain={[0, 'auto']}
                            tick={{ fontSize: 10 }}
                        />
                        <Radar
                            name={t('customer_summary.customers')}
                            dataKey="count"
                            stroke="#1A237E"
                            fill="#1A237E"
                            fillOpacity={0.2}
                            strokeWidth={2}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                            }}
                            formatter={(value: number) => [
                                value.toLocaleString(),
                                t('customer_summary.customers'),
                            ]}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            )}
        </div>
    )
}

export default IndustryRadarChart
